/**
 * Frontend-Spiegel der Backend-Ordner-Klassifikation
 * (`PropertyFilePathBuilder.Folders`): welche Ordner sind Basis-Gerüst (fix, geschützt) und
 * welche sind Custom-Ordner (umbenennbar/löschbar/darunter anlegbar). Muss mit dem Backend
 * synchron bleiben — Divergenz zeigt dem User ein Kontextmenü, das der Server dann ablehnt.
 */

/** Die fixen Scaffold-Unterordner (mirror `PropertyFilePathBuilder.ScaffoldFolders`). */
export const SCAFFOLD_FOLDERS: readonly string[] = [
  '01_Stammakte',
  '02_Vertraege/Aktiv',
  '02_Vertraege/Archiv',
  '03_Rechnungen',
  '04_Abrechnungen',
  '05_Steuer-Behoerde/Stamm',
  '06_Wartung-Reparatur',
  '07_Inventar/Listen',
  '07_Inventar/Garantien',
  '07_Inventar/Bedienungsanleitungen',
  '08_Fotos/Marketing',
  '08_Fotos/Zustand-Checkin',
  '08_Fotos/Inventar',
  '08_Fotos/Schaeden',
  '08_Fotos/Reinigung',
  '09_Schluessel-Zugang',
  '99_Sonstiges',
];

/** System-verwaltete Beleg-Subtrees (GoBD-Ausgangsrechnungen) — für User komplett tabu. */
const SYSTEM_FOLDER_PREFIXES: readonly string[] = ['03_Rechnungen/Ausgehend'];

const SCAFFOLD_SET = new Set(SCAFFOLD_FOLDERS);

/** Die zehn Top-Kategorien (erstes Segment der Scaffold-Ordner). */
export const CATEGORY_ROOTS: readonly string[] = Array.from(
  new Set(SCAFFOLD_FOLDERS.map((f) => f.split('/')[0]!)),
);

const CATEGORY_ROOT_SET = new Set(CATEGORY_ROOTS);

/** Segmente eines Ordner-Pfads ohne Leerteile. */
export function segmentsOf(folder: string): string[] {
  return folder.split('/').filter((s) => s.length > 0);
}

/** Top-Kategorie eines Pfads (erstes Segment), oder null bei leerem/Root-Pfad. */
export function categoryOf(folder: string): string | null {
  const segs = segmentsOf(folder);
  return segs.length > 0 ? segs[0]! : null;
}

/** Genau eine Top-Kategorie (`03_Rechnungen`) — kein Unterordner. */
export function isCategoryRoot(folder: string): boolean {
  const segs = segmentsOf(folder);
  return segs.length === 1 && CATEGORY_ROOT_SET.has(segs[0]!);
}

function isUnderSystemPrefix(folder: string): boolean {
  return SYSTEM_FOLDER_PREFIXES.some((sp) => folder === sp || folder.startsWith(sp + '/'));
}

/** System-verwalteter Ordner (GoBD-Ausgehend): kein Move-Ziel, kein Custom-Anlegen. */
export function isSystemFolder(folder: string): boolean {
  return isUnderSystemPrefix(folder);
}

/**
 * Gültiges Move-Ziel: selbe Top-Kategorie, ein echter Unterordner (nicht die bloße Kategorie-
 * Wurzel), nicht der aktuelle Ordner, kein System-Ordner. (Scaffold-Unterordner wie
 * `Aktiv`/`Archiv` sind erlaubt — anders als beim Rename/Delete.)
 */
export function isValidMoveTarget(sourceFolder: string, targetFolder: string): boolean {
  const category = categoryOf(sourceFolder);
  return (
    category !== null &&
    targetFolder !== sourceFolder &&
    isCategoryMoveTarget(category, targetFolder)
  );
}

/**
 * Geschützt = nicht umbenennbar/löschbar und kein gültiges Custom-Ziel: System-Ordner
 * (`_`-Präfix), fixe Scaffold-Unterordner, bloße Kategorie-Roots, GoBD-Ausgangs-Subtrees.
 */
export function isProtectedFolder(folder: string): boolean {
  if (!folder) return true;
  const segs = segmentsOf(folder);
  if (segs.length === 0 || segs[0]!.startsWith('_')) return true;
  if (SCAFFOLD_SET.has(folder)) return true;
  if (isUnderSystemPrefix(folder)) return true;
  return segs.length === 1 && CATEGORY_ROOT_SET.has(folder);
}

/**
 * Custom-Ordner: unter einer fixen Kategorie (≥ 2 Segmente, erstes ∈ Kategorie-Roots) und
 * nicht geschützt. Nur diese dürfen umbenannt/gelöscht werden und Unterordner tragen.
 */
export function isCustomFolder(folder: string): boolean {
  const segs = segmentsOf(folder);
  return segs.length >= 2 && CATEGORY_ROOT_SET.has(segs[0]!) && !isProtectedFolder(folder);
}

/**
 * Gültiges Bulk-Move-Ziel: ein echter Unterordner **einer bestimmten Top-Kategorie** (nicht die
 * Kategorie-Wurzel, kein System-Ordner). Anders als `isValidMoveTarget` ohne festen Quell-Ordner,
 * weil eine Mehrfach-Auswahl aus verschiedenen Ordnern derselben Kategorie stammen kann.
 */
export function isCategoryMoveTarget(category: string, targetFolder: string): boolean {
  return (
    categoryOf(targetFolder) === category &&
    !isCategoryRoot(targetFolder) &&
    !isSystemFolder(targetFolder)
  );
}

/**
 * Move-Leitplanke: eine Datei darf nur in einen Ordner **derselben Top-Kategorie** verschoben
 * werden. Cross-Kategorie geht ausschließlich per Reclassify.
 */
export function isSameCategory(sourceFolder: string, targetFolder: string): boolean {
  const a = categoryOf(sourceFolder);
  const b = categoryOf(targetFolder);
  return a !== null && a === b;
}
